import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { HustleState, JobInfo, Rarity, Variant } from "../types";
import { VARIANT_MULTIPLIER } from "../types";

type BattleSide = "player" | "enemy";
type BattlePhase = "idle" | "countdown" | "running" | "finished";
type CardAnimState = "idle" | "hit" | "ko";
type CalloutTone = "normal" | "crit" | "ko" | "tag";

interface BattleViewProps {
  data: HustleState | null;
  loading: boolean;
  onRefresh: () => void;
  onGoToHustle: () => void;
}

interface BattleFighter {
  id: string;
  name: string;
  nickname: string | null;
  imageUrl: string | null;
  rarity: Rarity;
  variant: Variant;
  jobName: string;
  company: string;
  maxHp: number;
  hp: number;
  power: number;
  attackPool: string[];
  specialAttack: string;
}

interface AttackCallout {
  key: number;
  text: string;
  tone: CalloutTone;
}

const RARITY_MULTIPLIER: Record<Rarity, number> = {
  common: 0.86,
  uncommon: 0.98,
  rare: 1.12,
  epic: 1.27,
  legendary: 1.42,
};

const RARITY_BORDER: Record<Rarity, string> = {
  common: "border-zinc-500/40",
  uncommon: "border-emerald-500/45",
  rare: "border-blue-500/45",
  epic: "border-fuchsia-500/45",
  legendary: "border-yellow-400/55",
};

const RARITY_TEXT: Record<Rarity, string> = {
  common: "text-zinc-300",
  uncommon: "text-emerald-300",
  rare: "text-blue-300",
  epic: "text-fuchsia-300",
  legendary: "text-yellow-300",
};

const VARIANT_SPECIAL: Record<Variant, string> = {
  normal: "Finisher",
  shiny: "Mirror Break",
  rainbow: "Prism Burst",
  glitch: "Null Crash",
};

const RARITY_SPECIAL_PREFIX: Record<Rarity, string> = {
  common: "Street",
  uncommon: "Campus",
  rare: "Elite",
  epic: "Mythic",
  legendary: "Godtier",
};

const NPC_TITLES = [
  "NPC",
  "CPU",
  "Arena Bot",
  "Shadow",
];

function initialForName(name: string) {
  const trimmed = name.replace(/^The\s+/i, "").trim();
  return trimmed.charAt(0).toUpperCase() || "M";
}

function tokenFromText(value: string, fallback: string) {
  const clean = value.replace(/[^a-zA-Z0-9 ]+/g, " ").trim();
  if (!clean) return fallback;
  const first = clean.split(/\s+/)[0];
  return first ?? fallback;
}

function makeAttackPool(job: JobInfo): { attackPool: string[]; specialAttack: string } {
  const role = tokenFromText(job.jobName, "Mog");
  const org = tokenFromText(job.company, "Squad");
  const pool = [
    `${role} Slam`,
    `${org} Rush`,
    `${job.rarity.toUpperCase()} Breaker`,
    `${job.variant.toUpperCase()} Combo`,
  ];
  const specialAttack = `${RARITY_SPECIAL_PREFIX[job.rarity]} ${VARIANT_SPECIAL[job.variant]}`;
  return { attackPool: pool, specialAttack };
}

function makeBattleFighter(job: JobInfo, battleId: string): BattleFighter {
  const variantMult = VARIANT_MULTIPLIER[job.variant];
  const rarityMult = RARITY_MULTIPLIER[job.rarity];
  const maxHp = Math.max(
    120,
    Math.round((job.baseRate * 11 + 140) * variantMult * (0.9 + rarityMult * 0.18))
  );
  const power = Math.max(18, Math.round((job.baseRate * 3 + 24) * variantMult * rarityMult));
  const { attackPool, specialAttack } = makeAttackPool(job);

  return {
    id: battleId,
    name: job.name,
    nickname: job.nickname,
    imageUrl: job.image_url,
    rarity: job.rarity,
    variant: job.variant,
    jobName: job.jobName,
    company: job.company,
    maxHp,
    hp: maxHp,
    power,
    attackPool,
    specialAttack,
  };
}

function buildEnemyTeam(data: HustleState, size: number): JobInfo[] {
  const sourcePool = [...data.bench, ...data.activeTeam];
  const pool = sourcePool.length > 0 ? sourcePool : data.activeTeam;
  if (pool.length === 0 || size <= 0) return [];

  const roster: JobInfo[] = [];
  for (let i = 0; i < size; i++) {
    const source = pool[(i * 2 + 1) % pool.length] ?? pool[0];
    if (!source) continue;

    const title = NPC_TITLES[i % NPC_TITLES.length] ?? "NPC";
    const powerScale = 0.88 + Math.random() * 0.24;
    const tunedBaseRate = Math.max(1, Math.round(source.baseRate * powerScale));

    roster.push({
      ...source,
      collectionId: -(source.collectionId + (i + 1) * 1000),
      name: `${source.name} ${title}`,
      baseRate: tunedBaseRate,
      earningsPerTick: tunedBaseRate,
      isActive: true,
    });
  }

  return roster;
}

function rollDamage(attacker: BattleFighter, isSpecial: boolean) {
  const variance = 0.85 + Math.random() * 0.35;
  const crit = Math.random() < (isSpecial ? 0.34 : 0.14);
  const critMult = crit ? 1.45 : 1;
  const specialMult = isSpecial ? 1.35 : 1;
  const damage = Math.max(
    12,
    Math.round(attacker.power * variance * critMult * specialMult)
  );
  return { damage, crit };
}

function takeDamage(
  team: BattleFighter[],
  fighterIndex: number,
  amount: number
): { updatedTeam: BattleFighter[]; hpAfter: number } {
  const fighter = team[fighterIndex];
  if (!fighter) return { updatedTeam: team, hpAfter: 0 };

  const hpAfter = Math.max(0, fighter.hp - amount);
  const updatedTeam = [...team];
  updatedTeam[fighterIndex] = { ...fighter, hp: hpAfter };
  return { updatedTeam, hpAfter };
}

function hpPct(fighter: BattleFighter | undefined) {
  if (!fighter) return 0;
  return Math.max(0, Math.min(100, (fighter.hp / fighter.maxHp) * 100));
}

function fighterClass(
  side: BattleSide,
  animState: CardAnimState,
  isClashing: boolean
) {
  return cn(
    "battle-fighter-card",
    side === "player" ? "battle-fighter-card--player" : "battle-fighter-card--enemy",
    isClashing && "battle-fighter-card--slam",
    animState === "hit" && "battle-fighter-card--hit",
    animState === "ko" && "battle-fighter-card--ko"
  );
}

function phaseLabel(phase: BattlePhase, winner: BattleSide | null) {
  if (phase === "countdown") return "Match Starting";
  if (phase === "running") return "Live";
  if (phase === "finished") return winner === "player" ? "Victory" : "Defeat";
  return "Ready";
}

function toneClass(tone: CalloutTone) {
  if (tone === "crit") return "battle-callout--crit";
  if (tone === "ko") return "battle-callout--ko";
  if (tone === "tag") return "battle-callout--tag";
  return "";
}

function QueueStrip({
  side,
  fighters,
}: {
  side: BattleSide;
  fighters: BattleFighter[];
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap gap-1.5",
        side === "player" ? "justify-start" : "justify-end"
      )}
    >
      {fighters.length === 0 ? (
        <span className="text-[10px] text-muted-foreground/80">No reserves</span>
      ) : (
        fighters.map((fighter) => (
          <span
            key={fighter.id}
            className={cn(
              "battle-queue-pill border px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide",
              RARITY_BORDER[fighter.rarity],
              RARITY_TEXT[fighter.rarity]
            )}
          >
            {fighter.name}
          </span>
        ))
      )}
    </div>
  );
}

function FighterAvatar({ fighter }: { fighter: BattleFighter }) {
  return (
    <div className="battle-fighter-avatar">
      {fighter.imageUrl ? (
        <img
          src={`/api/img?url=${encodeURIComponent(fighter.imageUrl)}`}
          alt={fighter.name}
          className="battle-fighter-avatar-image"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <span className="battle-fighter-avatar-fallback">
          {initialForName(fighter.name)}
        </span>
      )}
    </div>
  );
}

export function BattleView({
  data,
  loading,
  onRefresh,
  onGoToHustle,
}: BattleViewProps) {
  const [battlePhase, setBattlePhase] = useState<BattlePhase>("idle");
  const [countdown, setCountdown] = useState(3);
  const [playerTeam, setPlayerTeam] = useState<BattleFighter[]>([]);
  const [enemyTeam, setEnemyTeam] = useState<BattleFighter[]>([]);
  const [playerIndex, setPlayerIndex] = useState(0);
  const [enemyIndex, setEnemyIndex] = useState(0);
  const [turnSide, setTurnSide] = useState<BattleSide>("player");
  const [winner, setWinner] = useState<BattleSide | null>(null);
  const [isResolvingKo, setIsResolvingKo] = useState(false);
  const [isClashing, setIsClashing] = useState(false);
  const [playerCardState, setPlayerCardState] = useState<CardAnimState>("idle");
  const [enemyCardState, setEnemyCardState] = useState<CardAnimState>("idle");
  const [callout, setCallout] = useState<AttackCallout | null>(null);

  const timeoutRefs = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const calloutKeyRef = useRef(0);

  const queueSignature = useMemo(() => {
    if (!data) return "";
    return data.activeTeam.map((job) => `${job.collectionId}:${job.variant}`).join("|");
  }, [data]);

  const clearScheduled = useCallback(() => {
    for (const timeoutId of timeoutRefs.current) {
      clearTimeout(timeoutId);
    }
    timeoutRefs.current = [];
  }, []);

  const schedule = useCallback((fn: () => void, delayMs: number) => {
    const timeoutId = setTimeout(() => {
      timeoutRefs.current = timeoutRefs.current.filter((id) => id !== timeoutId);
      fn();
    }, delayMs);
    timeoutRefs.current.push(timeoutId);
    return timeoutId;
  }, []);

  const pushCallout = useCallback((text: string, tone: CalloutTone = "normal") => {
    calloutKeyRef.current += 1;
    setCallout({
      key: calloutKeyRef.current,
      text,
      tone,
    });
  }, []);

  const activePlayer = playerTeam[playerIndex];
  const activeEnemy = enemyTeam[enemyIndex];

  const playerAlive = playerTeam.filter((fighter, index) => index >= playerIndex && fighter.hp > 0).length;
  const enemyAlive = enemyTeam.filter((fighter, index) => index >= enemyIndex && fighter.hp > 0).length;

  const startBattle = useCallback(() => {
    clearScheduled();
    setWinner(null);
    setIsResolvingKo(false);
    setIsClashing(false);
    setPlayerCardState("idle");
    setEnemyCardState("idle");
    setTurnSide("player");

    if (!data || data.activeTeam.length === 0) {
      setBattlePhase("idle");
      setCallout(null);
      setPlayerTeam([]);
      setEnemyTeam([]);
      return;
    }

    const builtPlayerTeam = data.activeTeam.map((job, index) =>
      makeBattleFighter(job, `player-${job.collectionId}-${index}`)
    );
    const builtEnemyTeam = buildEnemyTeam(data, builtPlayerTeam.length).map((job, index) =>
      makeBattleFighter(job, `enemy-${Math.abs(job.collectionId)}-${index}`)
    );

    setPlayerTeam(builtPlayerTeam);
    setEnemyTeam(builtEnemyTeam);
    setPlayerIndex(0);
    setEnemyIndex(0);
    setCountdown(3);
    setBattlePhase("countdown");
    pushCallout("Arena primed. Lock in.", "tag");
  }, [clearScheduled, data, pushCallout]);

  const resolveKnockout = useCallback(
    (defenderSide: BattleSide, attackerName: string, defenderName: string) => {
      setIsResolvingKo(true);
      pushCallout(`${attackerName} KOs ${defenderName}!`, "ko");
      if (defenderSide === "player") {
        setPlayerCardState("ko");
      } else {
        setEnemyCardState("ko");
      }

      schedule(() => {
        if (defenderSide === "player") {
          const nextIndex = playerIndex + 1;
          if (nextIndex >= playerTeam.length) {
            setWinner("enemy");
            setBattlePhase("finished");
            setIsResolvingKo(false);
            return;
          }

          setPlayerIndex(nextIndex);
          setPlayerCardState("idle");
          const nextFighter = playerTeam[nextIndex];
          if (nextFighter) {
            pushCallout(`${nextFighter.name} tags in.`, "tag");
          }
          setTurnSide("player");
          setIsResolvingKo(false);
          return;
        }

        const nextIndex = enemyIndex + 1;
        if (nextIndex >= enemyTeam.length) {
          setWinner("player");
          setBattlePhase("finished");
          setIsResolvingKo(false);
          return;
        }

        setEnemyIndex(nextIndex);
        setEnemyCardState("idle");
        const nextFighter = enemyTeam[nextIndex];
        if (nextFighter) {
          pushCallout(`${nextFighter.name} tags in.`, "tag");
        }
        setTurnSide("enemy");
        setIsResolvingKo(false);
      }, 950);
    },
    [enemyIndex, enemyTeam, playerIndex, playerTeam, pushCallout, schedule]
  );

  const executeTurn = useCallback(() => {
    if (battlePhase !== "running") return;
    const attacker = turnSide === "player" ? playerTeam[playerIndex] : enemyTeam[enemyIndex];
    const defender = turnSide === "player" ? enemyTeam[enemyIndex] : playerTeam[playerIndex];

    if (!attacker || !defender) {
      if (!activeEnemy) {
        setWinner("player");
      } else if (!activePlayer) {
        setWinner("enemy");
      }
      setBattlePhase("finished");
      return;
    }

    const useSpecial = Math.random() < 0.24;
    const move = useSpecial
      ? attacker.specialAttack
      : attacker.attackPool[Math.floor(Math.random() * attacker.attackPool.length)] ??
        "Mog Slam";
    const { damage, crit } = rollDamage(attacker, useSpecial);
    const attackerLabel = attacker.name.replace(/\s+(NPC|CPU|Arena Bot|Shadow)$/i, "");

    pushCallout(
      `${attackerLabel} uses ${move}${crit ? " • CRIT" : ""}`,
      crit ? "crit" : "normal"
    );
    setIsClashing(true);
    if (turnSide === "player") {
      setEnemyCardState("hit");
    } else {
      setPlayerCardState("hit");
    }

    schedule(() => {
      setIsClashing(false);
    }, 340);

    schedule(() => {
      if (turnSide === "player") {
        const result = takeDamage(enemyTeam, enemyIndex, damage);
        setEnemyTeam(result.updatedTeam);
        if (result.hpAfter <= 0) {
          resolveKnockout("enemy", attacker.name, defender.name);
        } else {
          setEnemyCardState("idle");
          setTurnSide("enemy");
        }
        return;
      }

      const result = takeDamage(playerTeam, playerIndex, damage);
      setPlayerTeam(result.updatedTeam);
      if (result.hpAfter <= 0) {
        resolveKnockout("player", attacker.name, defender.name);
      } else {
        setPlayerCardState("idle");
        setTurnSide("player");
      }
    }, 320);
  }, [
    activeEnemy,
    activePlayer,
    battlePhase,
    enemyIndex,
    enemyTeam,
    playerIndex,
    playerTeam,
    pushCallout,
    resolveKnockout,
    schedule,
    turnSide,
  ]);

  useEffect(() => {
    return () => {
      clearScheduled();
    };
  }, [clearScheduled]);

  useEffect(() => {
    if (loading || !data) return;
    startBattle();
  }, [data, loading, queueSignature, startBattle]);

  useEffect(() => {
    if (battlePhase !== "countdown") return;
    if (countdown <= 0) {
      setBattlePhase("running");
      pushCallout("FIGHT!", "tag");
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 700);

    return () => clearTimeout(timer);
  }, [battlePhase, countdown, pushCallout]);

  useEffect(() => {
    if (battlePhase !== "running" || isResolvingKo) return;

    const turnTimer = setTimeout(() => {
      executeTurn();
    }, 980);

    return () => clearTimeout(turnTimer);
  }, [battlePhase, executeTurn, isResolvingKo]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-muted-foreground animate-pulse">Loading battle arena...</p>
      </div>
    );
  }

  if (data.activeTeam.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="border-border/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">No battle roster yet</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Your battle team uses the exact order from your active roster.
              Assign fighters in Hustle first, then come back to battle.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" onClick={onGoToHustle}>
                Set Team In Hustle
              </Button>
              <Button type="button" variant="outline" onClick={onRefresh}>
                Refresh Roster
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const playerQueue = playerTeam.slice(playerIndex + 1);
  const enemyQueue = enemyTeam.slice(enemyIndex + 1);

  return (
    <div className="max-w-6xl mx-auto px-4 py-7 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-2xl font-black tracking-tight">Battle Arena</h2>
          <p className="text-sm text-muted-foreground">
            Team order is locked from your active roster. Front fighters auto-clash until one side wipes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="uppercase tracking-wide text-[10px]">
            {phaseLabel(battlePhase, winner)}
          </Badge>
          <Button type="button" variant="outline" size="sm" onClick={onRefresh}>
            Refresh
          </Button>
          <Button type="button" size="sm" onClick={startBattle}>
            Rematch
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Your Team</p>
                <p className="text-base font-semibold">
                  {playerAlive} alive / {playerTeam.length}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">Turn: {turnSide === "player" ? "Yours" : "Enemy"}</p>
            </div>
            <div className="mt-3">
              <QueueStrip side="player" fighters={playerQueue} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Enemy Team</p>
                <p className="text-base font-semibold">
                  {enemyAlive} alive / {enemyTeam.length}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">Frontline AI</p>
            </div>
            <div className="mt-3">
              <QueueStrip side="enemy" fighters={enemyQueue} />
            </div>
          </CardContent>
        </Card>
      </div>

      <section className="battle-arena rounded-2xl border border-white/10 p-3 sm:p-6 overflow-hidden">
        <div className="battle-arena-backdrop" />
        <div className="battle-vs-mark">VS</div>

        {battlePhase === "countdown" && (
          <div className="battle-countdown">{countdown > 0 ? countdown : "FIGHT"}</div>
        )}

        {callout && (
          <div
            key={callout.key}
            className={cn("battle-callout", toneClass(callout.tone))}
          >
            {callout.text}
          </div>
        )}

        {winner && battlePhase === "finished" && (
          <div className={cn("battle-result-banner", winner === "player" ? "battle-result-win" : "battle-result-loss")}>
            {winner === "player" ? "YOU WIN" : "ENEMY WINS"}
          </div>
        )}

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-center min-h-[430px]">
          <article className={fighterClass("player", playerCardState, isClashing)}>
            {activePlayer ? (
              <>
                <div className="battle-card-head">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">Frontline</span>
                  <Badge className={cn("text-[10px]", RARITY_TEXT[activePlayer.rarity])}>
                    {activePlayer.rarity}
                  </Badge>
                </div>
                <FighterAvatar fighter={activePlayer} />
                <h3 className="battle-fighter-name">{activePlayer.name}</h3>
                {activePlayer.nickname && (
                  <p className="text-xs italic text-zinc-400">&ldquo;{activePlayer.nickname}&rdquo;</p>
                )}
                <div className="battle-hp-wrap">
                  <div className="battle-hp-track">
                    <div
                      className="battle-hp-fill battle-hp-fill-player"
                      style={{ width: `${hpPct(activePlayer)}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-zinc-300 mt-1">
                    {activePlayer.hp.toLocaleString()} / {activePlayer.maxHp.toLocaleString()} HP
                  </p>
                </div>
                <p className="text-xs text-zinc-400 mt-2">
                  {activePlayer.jobName} @ {activePlayer.company}
                </p>
              </>
            ) : (
              <div className="text-center text-zinc-400 text-sm">No fighter</div>
            )}
          </article>

          <article className={fighterClass("enemy", enemyCardState, isClashing)}>
            {activeEnemy ? (
              <>
                <div className="battle-card-head">
                  <Badge className="text-[10px] bg-red-600 text-white">CPU</Badge>
                  <Badge className={cn("text-[10px]", RARITY_TEXT[activeEnemy.rarity])}>
                    {activeEnemy.rarity}
                  </Badge>
                </div>
                <FighterAvatar fighter={activeEnemy} />
                <h3 className="battle-fighter-name">{activeEnemy.name}</h3>
                {activeEnemy.nickname && (
                  <p className="text-xs italic text-zinc-400">&ldquo;{activeEnemy.nickname}&rdquo;</p>
                )}
                <div className="battle-hp-wrap">
                  <div className="battle-hp-track">
                    <div
                      className="battle-hp-fill battle-hp-fill-enemy"
                      style={{ width: `${hpPct(activeEnemy)}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-zinc-300 mt-1">
                    {activeEnemy.hp.toLocaleString()} / {activeEnemy.maxHp.toLocaleString()} HP
                  </p>
                </div>
                <p className="text-xs text-zinc-400 mt-2">
                  {activeEnemy.jobName} @ {activeEnemy.company}
                </p>
              </>
            ) : (
              <div className="text-center text-zinc-400 text-sm">No fighter</div>
            )}
          </article>
        </div>
      </section>
    </div>
  );
}
